import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { splitNdjson } from '@/app/agentConversation/_helpers';
import { formatAndThrowApiError } from '@/helpers/api';

// A refused send arrives with its body still a stream, since the request asked
// for one. Read it as the JSON it is, so the error carries the API's own
// message (a 403, a 409 for a conversation that is still answering) the way
// every other request's does.
const withParsedErrorBody = async (error) => {
  const body = error.response?.data;
  if (!(body instanceof ReadableStream)) return error;
  try {
    error.response.data = await new Response(body).json();
  } catch {
    error.response.data = undefined;
  }
  return error;
};

/**
 * Sends a message and hands each event of the reply to `onEvent` as it
 * streams in (newline-delimited JSON — see AGENT_STREAM_EVENTS). Resolves once
 * the stream ends.
 *
 * Read through axios's fetch adapter so the global base URL, Authorization and
 * company-uid interceptors still apply. Aborting `signal` is how Stop works:
 * the server stops the turn and saves what it has, and this resolves quietly.
 */
// Stable identity: the panel's send callback depends on it.
const usePostAgentMessage = () => {
  const postAgentMessage = useCallback(async (payload, { signal, onEvent }) => {
    let reader;
    try {
      const response = await axios.post(`/agent-message`, payload, { adapter: 'fetch', responseType: 'stream', signal });
      reader = response.data.pipeThrough(new TextDecoderStream()).getReader();

      const read = async (buffer) => {
        const { value, done } = await reader.read();
        const { events, rest } = splitNdjson(done ? `${buffer}\n` : buffer + value);
        events.forEach(onEvent);
        return done ? undefined : read(rest);
      };
      await read('');
    } catch (error) {
      if (axios.isCancel(error) || signal?.aborted) return;
      toast.error('An error occurred sending the message');
      if (!error.response) throw error;
      formatAndThrowApiError(await withParsedErrorBody(error));
    } finally {
      // Releases the connection on every way out — a throw mid-stream included,
      // which would otherwise leave the response body open.
      reader?.cancel().catch(() => {});
    }
  }, []);

  return postAgentMessage;
};

export { usePostAgentMessage };
